import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { CustomDataMap } from '../../lib/subscriberCustomFields';
import type { NetworkingTaskRow } from '../../lib/networkingTasks';

type Props = {
  subscriberId: string;
  lastContactedAt?: string | null;
  customData?: CustomDataMap | null;
};

type BookingRow = {
  key: string;
  whenIso: string;
  pageLabel: string;
  relative: string;
  isPast: boolean;
  isToday: boolean;
};

const SLUG_LABELS: Record<string, string> = {
  'intro-call': 'Intro call',
  'office-hours': 'Office hours',
  'clarity-check': 'Tech Stack Clarity Check',
  book: 'Intro call',
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeMeetingLabel(iso: string, now = new Date()): { relative: string; isPast: boolean; isToday: boolean } {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) {
    return { relative: '', isPast: false, isToday: false };
  }
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((startDay.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return { relative: 'Today', isPast: false, isToday: true };
  if (days === 1) return { relative: 'Tomorrow', isPast: false, isToday: false };
  if (days > 1) return { relative: `In ${days} days`, isPast: false, isToday: false };
  if (days === -1) return { relative: 'Yesterday', isPast: true, isToday: false };
  return { relative: `${Math.abs(days)} days ago`, isPast: true, isToday: false };
}

function pageLabelFromMeta(meta: Record<string, unknown>): string {
  const name =
    typeof meta.booking_page_name === 'string' && meta.booking_page_name.trim()
      ? meta.booking_page_name.trim()
      : '';
  if (name) return name;
  const slug = typeof meta.booking_slug === 'string' ? meta.booking_slug.trim() : '';
  if (slug) return SLUG_LABELS[slug] ?? slug;
  return 'BookTime meeting';
}

function bookingFromTask(task: NetworkingTaskRow): BookingRow | null {
  const meta = task.metadata ?? {};
  const whenIso =
    (typeof meta.booking_start === 'string' && meta.booking_start) ||
    (typeof task.due_at === 'string' && task.due_at) ||
    '';
  if (!whenIso) return null;
  const eventId =
    typeof meta.calendar_event_id === 'string' && meta.calendar_event_id.trim()
      ? meta.calendar_event_id.trim()
      : null;
  const rel = relativeMeetingLabel(whenIso);
  return {
    key: eventId || task.id,
    whenIso,
    pageLabel: pageLabelFromMeta(meta),
    relative: rel.relative,
    isPast: rel.isPast,
    isToday: rel.isToday,
  };
}

function customFieldString(customData: CustomDataMap | null | undefined, key: string): string | null {
  const v = customData?.[key];
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) {
    const inner = (v as { value?: unknown }).value;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  }
  return null;
}

export default function SubscriberBookingsSection({
  subscriberId,
  lastContactedAt,
  customData,
}: Props) {
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!subscriberId) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, due_at, source, metadata, created_at')
        .eq('subscriber_id', subscriberId)
        .eq('source', 'booktime')
        .order('due_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load bookings');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const bookings = useMemo(() => {
    const rows: BookingRow[] = [];
    const seen = new Set<string>();
    for (const task of tasks) {
      const row = bookingFromTask(task);
      if (!row) continue;
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      rows.push(row);
    }
    rows.sort((a, b) => new Date(b.whenIso).getTime() - new Date(a.whenIso).getTime());
    return rows;
  }, [tasks]);

  const lastMetIso = useMemo(() => {
    // Prefer most recent past/today meeting — not a future booking.
    const pastOrToday = bookings.find((b) => b.isPast || b.isToday);
    if (pastOrToday?.whenIso) return pastOrToday.whenIso;
    const fromField = customFieldString(customData, 'booktime_last_booking_at');
    if (fromField) {
      const rel = relativeMeetingLabel(fromField);
      if (rel.isPast || rel.isToday) return fromField;
    }
    if (typeof lastContactedAt === 'string' && lastContactedAt.trim()) {
      const iso = lastContactedAt.trim();
      const rel = relativeMeetingLabel(iso);
      if (rel.isPast || rel.isToday) return iso;
    }
    return null;
  }, [bookings, customData, lastContactedAt]);

  const lastMetRel = lastMetIso ? relativeMeetingLabel(lastMetIso) : null;
  const lastSlug = customFieldString(customData, 'booktime_last_booking_slug');

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-sky-500" />
          Bookings
          {bookings.length > 0 ? (
            <span className="normal-case font-medium text-gray-500 dark:text-gray-400">
              ({bookings.length})
            </span>
          ) : null}
        </h2>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          aria-label="Refresh bookings"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {lastMetIso ? (
        <div className="px-6 py-3 bg-sky-50/80 dark:bg-sky-950/30 border-b border-sky-100 dark:border-sky-900/40">
          <p className="text-xs font-medium uppercase tracking-wide text-sky-800 dark:text-sky-200">
            Last met
          </p>
          <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
            {formatWhen(lastMetIso)}
            {lastMetRel?.relative ? (
              <span className="ml-2 font-medium text-sky-700 dark:text-sky-300">
                · {lastMetRel.relative}
              </span>
            ) : null}
          </p>
          {lastSlug && bookings.length === 0 ? (
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              {SLUG_LABELS[lastSlug] ?? lastSlug}
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 px-6 py-6 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading bookings…
        </div>
      ) : bookings.length === 0 ? (
        <p className="px-6 py-5 text-sm text-gray-500 dark:text-gray-400">
          No BookTime bookings on file for this connection yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {bookings.map((row) => (
            <li
              key={row.key}
              className="px-6 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{row.pageLabel}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{formatWhen(row.whenIso)}</p>
              </div>
              <span
                className={[
                  'self-start sm:self-center text-xs font-medium px-2 py-0.5 rounded-full shrink-0',
                  row.isToday
                    ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                    : row.isPast
                      ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                      : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
                ].join(' ')}
              >
                {row.relative}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!loading && bookings.length > 0 ? (
        <p className="px-6 py-2.5 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700/80">
          From BookTime bookings (newest first)
        </p>
      ) : null}
    </section>
  );
}
